import { FC } from 'react';
import { Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { NAMESPACE } from '@/constants';
import { formatMoney } from '@/constants/currency';
import type { RemovedLineItem } from '@/models';

type Props = {
  items?: RemovedLineItem[];
  planCurrency?: string;
};

const formatDate = (value?: unknown) => (value ? dayjs(String(value)).format('DD MMM YYYY') : '—');

/**
 * Line items taken off the plan after it was booked.
 *
 * They are gone from the list, the totals and the booking sheet, but the
 * signed IO still prints them — so the plan keeps saying what they were, who
 * removed them and why, rather than leaving the IO looking wrong.
 */
const RemovedLineItems: FC<Props> = ({ items, planCurrency }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  if (!items?.length) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <Typography.Title level={5} style={{ marginBottom: 4 }}>
        {t('removedLineItemsTitle')}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t('removedLineItemsHint')}</Typography.Paragraph>
      <Table<RemovedLineItem>
        size="small"
        pagination={false}
        rowKey={(item) => `${item.lineItemId}-${item.removedAt}`}
        dataSource={items.slice().reverse()}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: t('removedLineItemsLine'),
            key: 'line',
            render: (_, { snapshot }) =>
              [snapshot.channel, snapshot.product, snapshot.dsp].filter(Boolean).join(' · ') || '—'
          },
          {
            title: t('removedLineItemsBudget'),
            key: 'budget',
            render: (_, { snapshot }) =>
              snapshot.budget != null
                ? formatMoney(Number(snapshot.budget), (snapshot.currency as string | undefined) ?? planCurrency)
                : '—'
          },
          {
            title: t('removedLineItemsFlight'),
            key: 'flight',
            render: (_, { snapshot }) =>
              `${formatDate(snapshot.startDate)} – ${formatDate(snapshot.endDate)}`
          },
          { title: t('removedLineItemsBy'), dataIndex: 'removedBy', key: 'removedBy' },
          {
            title: t('removedLineItemsAt'),
            key: 'removedAt',
            render: (_, { removedAt }) => dayjs(removedAt).format('DD MMM YYYY, HH:mm')
          },
          { title: t('removedLineItemsReason'), dataIndex: 'reason', key: 'reason' }
        ]}
      />
    </div>
  );
};

export default RemovedLineItems;
